// Types for the supply classes data
export interface Issue {
  type: 'warning' | 'error'
  message: string
}
export interface UnitUpload {
  battalion: string
  company: string
  fileName: string
  uploadTime: string
  status: 'success' | 'warning' | 'error' | 'pending'
  statusMessage?: string
}
export interface Source {
  id: string
  type: 'gcss-a' | 'jbcp' | 'excel' | 'other'
  name: string
  status: 'healthy' | 'delay' | 'failure' | 'unknown'
  lastUpdated: string
  fileCount24h: number
  fileCountWeek: number
  issues?: Issue[]
  unitUploads?: UnitUpload[]
}
export interface SupplyClass {
  id: string
  romanNumeral: string
  name: string
  icon: string
  sources: Source[]
}
// Mock data for supply classes
export const supplyClassesData: SupplyClass[] = [
  {
    id: 'class-i',
    romanNumeral: 'I',
    name: 'Rations',
    icon: '🍽️',
    sources: [
      {
        id: 'class-i-gcss-a',
        type: 'gcss-a',
        name: 'GCSS-A',
        status: 'healthy',
        lastUpdated: '2023-06-15 08:30 AM',
        fileCount24h: 12,
        fileCountWeek: 84,
        issues: [],
      },
      {
        id: 'class-i-jbcp',
        type: 'jbcp',
        name: 'JBCP',
        status: 'delay',
        lastUpdated: '2023-06-14 11:45 PM',
        fileCount24h: 8,
        fileCountWeek: 56,
        issues: [
          {
            type: 'warning',
            message: 'Delayed sync by 2 hours',
          },
        ],
      },
      {
        id: 'class-i-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'healthy',
        lastUpdated: '2023-06-15 09:15 AM',
        fileCount24h: 24,
        fileCountWeek: 120,
        unitUploads: [
          {
            battalion: '1st Battalion',
            company: 'Alpha Company',
            fileName: 'rations_inventory_a_co.xlsx',
            uploadTime: '2023-06-15 08:45 AM',
            status: 'success',
          },
          {
            battalion: '1st Battalion',
            company: 'Bravo Company',
            fileName: 'rations_inventory_b_co.xlsx',
            uploadTime: '2023-06-15 07:30 AM',
            status: 'success',
          },
          {
            battalion: '2nd Battalion',
            company: 'Charlie Company',
            fileName: 'rations_inventory_c_co.xlsx',
            uploadTime: '2023-06-15 09:15 AM',
            status: 'warning',
            statusMessage: 'Missing fields',
          },
          {
            battalion: '2nd Battalion',
            company: 'Delta Company',
            fileName: 'rations_inventory_d_co.xlsx',
            uploadTime: '2023-06-14 04:20 PM',
            status: 'success',
          },
        ],
      },
    ],
  },
  {
    id: 'class-ii',
    romanNumeral: 'II',
    name: 'Clothing and Equipment',
    icon: '👕',
    sources: [
      {
        id: 'class-ii-gcss-a',
        type: 'gcss-a',
        name: 'GCSS-A',
        status: 'healthy',
        lastUpdated: '2023-06-15 07:45 AM',
        fileCount24h: 18,
        fileCountWeek: 112,
        issues: [],
      },
      {
        id: 'class-ii-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'delay',
        lastUpdated: '2023-06-14 06:30 PM',
        fileCount24h: 15,
        fileCountWeek: 95,
        unitUploads: [
          {
            battalion: '1st Battalion',
            company: 'Alpha Company',
            fileName: 'clothing_equipment_a_co.xlsx',
            uploadTime: '2023-06-14 05:30 PM',
            status: 'success',
          },
          {
            battalion: '1st Battalion',
            company: 'Bravo Company',
            fileName: 'clothing_equipment_b_co.xlsx',
            uploadTime: '2023-06-14 06:15 PM',
            status: 'error',
            statusMessage: 'Format error',
          },
          {
            battalion: '3rd Battalion',
            company: 'Echo Company',
            fileName: 'clothing_equipment_e_co.xlsx',
            uploadTime: '2023-06-14 04:45 PM',
            status: 'success',
          },
        ],
      },
    ],
  },
  {
    id: 'class-iii',
    romanNumeral: 'III',
    name: 'POL',
    icon: '⛽',
    sources: [
      {
        id: 'class-iii-gcss-a',
        type: 'gcss-a',
        name: 'GCSS-A',
        status: 'failure',
        lastUpdated: '2023-06-14 02:15 PM',
        fileCount24h: 5,
        fileCountWeek: 78,
        issues: [
          {
            type: 'error',
            message: 'API connection timeout',
          },
          {
            type: 'error',
            message: 'Missing last 6 hours of data',
          },
        ],
      },
      {
        id: 'class-iii-jbcp',
        type: 'jbcp',
        name: 'JBCP',
        status: 'healthy',
        lastUpdated: '2023-06-15 08:00 AM',
        fileCount24h: 24,
        fileCountWeek: 168,
        issues: [],
      },
      {
        id: 'class-iii-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'healthy',
        lastUpdated: '2023-06-15 07:30 AM',
        fileCount24h: 12,
        fileCountWeek: 84,
        unitUploads: [
          {
            battalion: '1st Battalion',
            company: 'Alpha Company',
            fileName: 'fuel_consumption_a_co.xlsx',
            uploadTime: '2023-06-15 07:15 AM',
            status: 'success',
          },
          {
            battalion: '2nd Battalion',
            company: 'Charlie Company',
            fileName: 'fuel_consumption_c_co.xlsx',
            uploadTime: '2023-06-15 06:45 AM',
            status: 'success',
          },
          {
            battalion: '3rd Battalion',
            company: 'Echo Company',
            fileName: 'fuel_consumption_e_co.xlsx',
            uploadTime: '2023-06-15 07:30 AM',
            status: 'pending',
          },
        ],
      },
    ],
  },
  {
    id: 'class-iv',
    romanNumeral: 'IV',
    name: 'Construction Materials',
    icon: '🏗️',
    sources: [
      {
        id: 'class-iv-gcss-a',
        type: 'gcss-a',
        name: 'GCSS-A',
        status: 'healthy',
        lastUpdated: '2023-06-15 06:30 AM',
        fileCount24h: 8,
        fileCountWeek: 56,
        issues: [],
      },
      {
        id: 'class-iv-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'healthy',
        lastUpdated: '2023-06-15 08:45 AM',
        fileCount24h: 6,
        fileCountWeek: 42,
        unitUploads: [
          {
            battalion: 'Engineering Battalion',
            company: 'Alpha Company',
            fileName: 'construction_materials_a_co.xlsx',
            uploadTime: '2023-06-15 08:30 AM',
            status: 'success',
          },
          {
            battalion: 'Engineering Battalion',
            company: 'Bravo Company',
            fileName: 'construction_materials_b_co.xlsx',
            uploadTime: '2023-06-15 08:45 AM',
            status: 'warning',
            statusMessage: 'Incomplete data',
          },
        ],
      },
    ],
  },
  {
    id: 'class-v',
    romanNumeral: 'V',
    name: 'Ammunition',
    icon: '🔫',
    sources: [
      {
        id: 'class-v-gcss-a',
        type: 'gcss-a',
        name: 'GCSS-A',
        status: 'delay',
        lastUpdated: '2023-06-14 11:30 PM',
        fileCount24h: 16,
        fileCountWeek: 112,
        issues: [
          {
            type: 'warning',
            message: 'Delayed sync by 4 hours',
          },
        ],
      },
      {
        id: 'class-v-jbcp',
        type: 'jbcp',
        name: 'JBCP',
        status: 'healthy',
        lastUpdated: '2023-06-15 07:15 AM',
        fileCount24h: 24,
        fileCountWeek: 168,
        issues: [],
      },
      {
        id: 'class-v-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'healthy',
        lastUpdated: '2023-06-15 09:00 AM',
        fileCount24h: 18,
        fileCountWeek: 126,
        unitUploads: [
          {
            battalion: '1st Battalion',
            company: 'Alpha Company',
            fileName: 'ammo_inventory_a_co.xlsx',
            uploadTime: '2023-06-15 08:15 AM',
            status: 'success',
          },
          {
            battalion: '1st Battalion',
            company: 'Bravo Company',
            fileName: 'ammo_inventory_b_co.xlsx',
            uploadTime: '2023-06-15 08:30 AM',
            status: 'success',
          },
          {
            battalion: '2nd Battalion',
            company: 'Charlie Company',
            fileName: 'ammo_inventory_c_co.xlsx',
            uploadTime: '2023-06-15 08:45 AM',
            status: 'error',
            statusMessage: 'Invalid format',
          },
          {
            battalion: '2nd Battalion',
            company: 'Delta Company',
            fileName: 'ammo_inventory_d_co.xlsx',
            uploadTime: '2023-06-15 09:00 AM',
            status: 'success',
          },
        ],
      },
    ],
  },
  {
    id: 'class-vi',
    romanNumeral: 'VI',
    name: 'Personal Demand Items',
    icon: '🧴',
    sources: [
      {
        id: 'class-vi-gcss-a',
        type: 'gcss-a',
        name: 'GCSS-A',
        status: 'healthy',
        lastUpdated: '2023-06-15 07:00 AM',
        fileCount24h: 6,
        fileCountWeek: 42,
        issues: [],
      },
      {
        id: 'class-vi-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'healthy',
        lastUpdated: '2023-06-15 08:00 AM',
        fileCount24h: 9,
        fileCountWeek: 63,
        unitUploads: [
          {
            battalion: '1st Battalion',
            company: 'Alpha Company',
            fileName: 'personal_items_a_co.xlsx',
            uploadTime: '2023-06-15 07:45 AM',
            status: 'success',
          },
          {
            battalion: '2nd Battalion',
            company: 'Charlie Company',
            fileName: 'personal_items_c_co.xlsx',
            uploadTime: '2023-06-15 08:00 AM',
            status: 'success',
          },
        ],
      },
    ],
  },
  {
    id: 'class-vii',
    romanNumeral: 'VII',
    name: 'Major Items',
    icon: '🚜',
    sources: [
      {
        id: 'class-vii-gcss-a',
        type: 'gcss-a',
        name: 'GCSS-A',
        status: 'healthy',
        lastUpdated: '2023-06-15 08:15 AM',
        fileCount24h: 12,
        fileCountWeek: 84,
        issues: [],
      },
      {
        id: 'class-vii-jbcp',
        type: 'jbcp',
        name: 'JBCP',
        status: 'healthy',
        lastUpdated: '2023-06-15 07:30 AM',
        fileCount24h: 8,
        fileCountWeek: 56,
        issues: [],
      },
      {
        id: 'class-vii-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'delay',
        lastUpdated: '2023-06-14 05:45 PM',
        fileCount24h: 4,
        fileCountWeek: 28,
        unitUploads: [
          {
            battalion: '1st Battalion',
            company: 'Alpha Company',
            fileName: 'major_items_a_co.xlsx',
            uploadTime: '2023-06-14 05:30 PM',
            status: 'success',
          },
          {
            battalion: '1st Battalion',
            company: 'Bravo Company',
            fileName: 'major_items_b_co.xlsx',
            uploadTime: '2023-06-14 05:45 PM',
            status: 'warning',
            statusMessage: 'Missing maintenance data',
          },
        ],
      },
    ],
  },
  {
    id: 'class-viii',
    romanNumeral: 'VIII',
    name: 'Medical Materiel',
    icon: '🩺',
    sources: [
      {
        id: 'class-viii-gcss-a',
        type: 'gcss-a',
        name: 'GCSS-A',
        status: 'delay',
        lastUpdated: '2023-06-14 10:15 PM',
        fileCount24h: 10,
        fileCountWeek: 70,
        issues: [
          {
            type: 'warning',
            message: 'Delayed sync by 3 hours',
          },
        ],
      },
      {
        id: 'class-viii-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'healthy',
        lastUpdated: '2023-06-15 08:30 AM',
        fileCount24h: 15,
        fileCountWeek: 105,
        unitUploads: [
          {
            battalion: 'Medical Battalion',
            company: 'Alpha Company',
            fileName: 'medical_supplies_a_co.xlsx',
            uploadTime: '2023-06-15 08:15 AM',
            status: 'success',
          },
          {
            battalion: 'Medical Battalion',
            company: 'Bravo Company',
            fileName: 'medical_supplies_b_co.xlsx',
            uploadTime: '2023-06-15 08:30 AM',
            status: 'success',
          },
          {
            battalion: '1st Battalion',
            company: 'Medical Platoon',
            fileName: 'medical_supplies_1bn.xlsx',
            uploadTime: '2023-06-15 07:45 AM',
            status: 'error',
            statusMessage: 'Missing required sheets',
          },
        ],
      },
    ],
  },
  {
    id: 'class-ix',
    romanNumeral: 'IX',
    name: 'Repair Parts',
    icon: '🔧',
    sources: [
      {
        id: 'class-ix-gcss-a',
        type: 'gcss-a',
        name: 'GCSS-A',
        status: 'healthy',
        lastUpdated: '2023-06-15 07:45 AM',
        fileCount24h: 24,
        fileCountWeek: 168,
        issues: [],
      },
      {
        id: 'class-ix-jbcp',
        type: 'jbcp',
        name: 'JBCP',
        status: 'failure',
        lastUpdated: '2023-06-14 01:30 PM',
        fileCount24h: 3,
        fileCountWeek: 94,
        issues: [
          {
            type: 'error',
            message: 'Authentication failure',
          },
          {
            type: 'error',
            message: 'Missing data since 2023-06-14 01:30 PM',
          },
        ],
      },
      {
        id: 'class-ix-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'healthy',
        lastUpdated: '2023-06-15 09:15 AM',
        fileCount24h: 18,
        fileCountWeek: 126,
        unitUploads: [
          {
            battalion: 'Maintenance Battalion',
            company: 'Alpha Company',
            fileName: 'repair_parts_a_co.xlsx',
            uploadTime: '2023-06-15 08:45 AM',
            status: 'success',
          },
          {
            battalion: 'Maintenance Battalion',
            company: 'Bravo Company',
            fileName: 'repair_parts_b_co.xlsx',
            uploadTime: '2023-06-15 09:00 AM',
            status: 'success',
          },
          {
            battalion: 'Maintenance Battalion',
            company: 'Charlie Company',
            fileName: 'repair_parts_c_co.xlsx',
            uploadTime: '2023-06-15 09:15 AM',
            status: 'success',
          },
        ],
      },
    ],
  },
  {
    id: 'class-x',
    romanNumeral: 'X',
    name: 'Nonmilitary',
    icon: '🏠',
    sources: [
      {
        id: 'class-x-other',
        type: 'other',
        name: 'Civil Affairs Database',
        status: 'unknown',
        lastUpdated: '2023-06-14 12:00 PM',
        fileCount24h: 2,
        fileCountWeek: 14,
        issues: [
          {
            type: 'warning',
            message: 'Infrequent updates',
          },
        ],
      },
      {
        id: 'class-x-excel',
        type: 'excel',
        name: 'Excel Sheets',
        status: 'healthy',
        lastUpdated: '2023-06-15 08:00 AM',
        fileCount24h: 5,
        fileCountWeek: 35,
        unitUploads: [
          {
            battalion: 'Civil Affairs Battalion',
            company: 'Alpha Company',
            fileName: 'nonmilitary_support_a_co.xlsx',
            uploadTime: '2023-06-15 07:45 AM',
            status: 'success',
          },
          {
            battalion: 'Civil Affairs Battalion',
            company: 'Bravo Company',
            fileName: 'nonmilitary_support_b_co.xlsx',
            uploadTime: '2023-06-15 08:00 AM',
            status: 'warning',
            statusMessage: 'Missing location data',
          },
        ],
      },
    ],
  },
]
